/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React, { Fragment } from "react"
import { useTranslationsMap, useLocaleContext } from "common/utils/i18n"
import {
  DEFAULT_DATE_FORMAT,
  getDayjsLocale,
  getLocalizedDateFormat,
  TAIWAN_DATE_FORMAT,
} from "utils/i18n"
import dayjs from "dayjs"
import { Review, ReviewUnReportableReason } from "types/api/menuFeedback.type"
import { TickMarkFilled, WarningFilled } from "@duxton-web/icons"

type DrawerReviewStatusProps = {
  review: Review
}

type StyleObject = {
  [key: string]: any
}

const DrawerReviewStatus: React.FC<DrawerReviewStatusProps> = ({ review }) => {
  const generateStatusClassStyle = (additionalStyle: StyleObject) => {
    return css({
      margin: "35px 25px",
      display: "flex",
      flexDirection: "row",
      gap: "8px",
      alignItems: "center",
      padding: "16px",
      width: "430px",
      height: "68px",
      borderRadius: "16px",
      ...additionalStyle,
    })
  }

  const textMap = useTranslationsMap({
    reported: "feedback.body.reviewStatus.reported",
    public: "feedback.body.reviewStatus.public",
    removed: "feedback.body.reviewStatus.removed",
  })

  const locale = useLocaleContext("locale")
  const dayjsLocale = getDayjsLocale(locale)
  const dateFormat = getLocalizedDateFormat(
    locale,
    DEFAULT_DATE_FORMAT,
    TAIWAN_DATE_FORMAT,
  )

  switch (review.unreportableReason) {
    case ReviewUnReportableReason.ReviewUnreportableReasonReviewIsRejected:
      return (
        <div css={generateStatusClassStyle({ background: "#FFF9C0" })}>
          <TickMarkFilled style={{ color: "#f09800", fontSize: 18 }} />
          {textMap.public}
        </div>
      )
    case ReviewUnReportableReason.ReviewUnreportableReasonReviewIsReported:
      return (
        <div css={generateStatusClassStyle({ background: "#FDF2F2" })}>
          <WarningFilled style={{ color: "#f55538", fontSize: 26 }} />
          {textMap.reported.replace(
            "<date.short>",
            `${dayjs(review.reportedAt)
              .locale(dayjsLocale)
              .format(dateFormat)}`,
          )}
        </div>
      )
    case ReviewUnReportableReason.ReviewUnreportableReasonReviewIsRemoved:
      return (
        <div css={generateStatusClassStyle({ background: "#FFF9C0" })}>
          <TickMarkFilled style={{ color: "#f09800", fontSize: 18 }} />
          {textMap.removed}
        </div>
      )
    default:
      return <Fragment />
  }
}

export default React.memo(DrawerReviewStatus)
